import React, { useState, useEffect } from "react";
import { Dialog, Box, Button } from "@mui/material";
import "react-pdf/dist/Page/TextLayer.css";
import { PDFDocument } from "pdf-lib";
import PropTypes from "prop-types";

const revokeUrls = (urls) => {
  urls.forEach((url) => URL.revokeObjectURL(url));
};

const isCrossOriginUrl = (url) => {
  if (!url || typeof window === "undefined") return false;

  try {
    const parsedUrl = new URL(url, window.location.href);
    return parsedUrl.origin !== window.location.origin;
  } catch (_error) {
    return false;
  }
};

const PdfViewer = ({
  open,
  onClose,
  previewImage,
  useDialog,
  isPdf,
  setPageNumber,
  pageNumber,
  isVideo,
  file
}) => {
  const [fileType, setFileType] = useState("");
  const [totalPdfPages, setTotalPdfPages] = useState(1);
  const [pdfPageUrls, setPdfPageUrls] = useState([]);
  const [isPreparingPages, setIsPreparingPages] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");

  const getFileType = (url) => {
    if (/\.pdf(\?|$)/i.test(url || "")) {
      return "pdf";
    } else if (/\.(jpg|jpeg|png)(\?|$)/i.test(url || "")) {
      return "image";
    } else if (/\.video(\?|$)/i.test(url || "")) {
      return "video";
    } else {
      return "";
    }
  };

  // Set the file type when the component mounts
  useEffect(() => {
    setFileType(getFileType(previewImage));
  }, [previewImage]);

  useEffect(() => {
    if (!isPdf || !previewImage || !isCrossOriginUrl(previewImage)) {
      setLocalPreviewUrl("");
      return undefined;
    }

    const isBrowserFile =
      (typeof File !== "undefined" && file instanceof File) ||
      (typeof Blob !== "undefined" && file instanceof Blob);

    if (!isBrowserFile) {
      setLocalPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file, isPdf, previewImage]);

  const effectivePreviewImage = localPreviewUrl || previewImage;

  useEffect(() => {
    if (!open || !isPdf || !effectivePreviewImage) {
      setPdfPageUrls((prevUrls) => {
        revokeUrls(prevUrls);
        return [];
      });
      setIsPreparingPages(false);
      setTotalPdfPages(1);
      return;
    }

    if (isCrossOriginUrl(effectivePreviewImage)) {
      setPdfPageUrls((prevUrls) => {
        revokeUrls(prevUrls);
        return [];
      });
      setIsPreparingPages(false);
      setTotalPdfPages(1);
      setPageNumber(1);
      return;
    }

    let isMounted = true;
    setPageNumber(1);
    setIsPreparingPages(true);

    const preparePdfPages = async () => {
      try {
        const response = await fetch(effectivePreviewImage);
        const bytes = await response.arrayBuffer();
        const sourcePdf = await PDFDocument.load(bytes);
        const pages = sourcePdf.getPageCount() || 1;
        const generatedUrls = [];

        for (let index = 0; index < pages; index += 1) {
          const singlePagePdf = await PDFDocument.create();
          const [copiedPage] = await singlePagePdf.copyPages(sourcePdf, [index]);
          singlePagePdf.addPage(copiedPage);
          const singlePageBytes = await singlePagePdf.save();
          const pageBlob = new Blob([singlePageBytes], { type: "application/pdf" });
          generatedUrls.push(URL.createObjectURL(pageBlob));
        }

        if (isMounted) {
          setPdfPageUrls((prevUrls) => {
            revokeUrls(prevUrls);
            return generatedUrls;
          });
          setTotalPdfPages(pages);
        } else {
          revokeUrls(generatedUrls);
        }
      } catch (_error) {
        if (isMounted) {
          setPdfPageUrls((prevUrls) => {
            revokeUrls(prevUrls);
            return [];
          });
          setTotalPdfPages(1);
          setPageNumber(1);
        }
      } finally {
        if (isMounted) {
          setIsPreparingPages(false);
        }
      }
    };

    preparePdfPages();

    return () => {
      isMounted = false;
    };
  }, [open, isPdf, effectivePreviewImage, setPageNumber]);

  const previousPage = () => {
    setPageNumber((prevPageNumber) => Math.max(1, prevPageNumber - 1));
  };

  const nextPage = () => {
    setPageNumber((prevPageNumber) =>
      Math.min(totalPdfPages, prevPageNumber + 1)
    );
  };

  const currentPageUrl = pdfPageUrls[(pageNumber || 1) - 1];
  const pdfPreviewUrl = currentPageUrl
    ? `${currentPageUrl}#zoom=page-fit&toolbar=0&navpanes=0&scrollbar=0`
    : `${effectivePreviewImage}#page=${pageNumber || 1}&zoom=page-fit&toolbar=0&navpanes=0&scrollbar=0`;

  let dialogContent;
  if (isPdf) {
    dialogContent = (
      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f7f8fa",
            minHeight: "40rem",
          }}
        >
          <object
            key={`pdf-page-${pageNumber || 1}`}
            data={pdfPreviewUrl}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{ minHeight: "40rem", border: "none" }}
          >
            <embed
              src={pdfPreviewUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ minHeight: "40rem", border: "none" }}
            />
          </object>
          {isPreparingPages && (
            <Box
              sx={{
                position: "absolute",
                color: "#4b5563",
                backgroundColor: "rgba(255,255,255,0.75)",
                px: 1,
                py: 0.5,
                borderRadius: "4px",
              }}
            >
              Preparing pages...
            </Box>
          )}
          {!isPreparingPages && !pdfPreviewUrl && (
            <Box sx={{ color: "#4b5563" }}>
              Unable to load PDF preview.
            </Box>
          )}
        </Box>
        {totalPdfPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              py: 1,
              flexShrink: 0,
            }}
          >
            <Button
              onClick={previousPage}
              disabled={(pageNumber || 1) <= 1}
              variant="contained"
              color="primary"
            >
              Previous
            </Button>
            <Button
              onClick={nextPage}
              disabled={(pageNumber || 1) >= totalPdfPages}
              variant="contained"
              color="primary"
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
    );
  } else if (isVideo) {
    dialogContent = (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        height: '100%',
        width: '100%'
      }}>
        <video
          controls
          style={{
            maxWidth: '100%',
            maxHeight: '80vh',
            width: '100%',
            height: 'auto'
          }}
        >
          <source src={previewImage} type={file?.type} />
          <track kind="captions" srcLang="en" label="English captions" />
          Your browser does not support the video tag.
        </video>
      </Box>
    );
  } else {
    dialogContent = (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        height: '100%',
        width: '100%'
      }}>
        <img
          src={previewImage}
          alt="Preview"
          style={{
            maxWidth: '100%',
            maxHeight: '80vh',
            objectFit: 'contain'
          }}
        />
      </Box>
    );
  }

  if (useDialog) {
    return (
      <Dialog open={open} onClose={onClose}
      maxWidth="md" 
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '90vh',
          maxWidth: '600px',
          overflow: 'hidden',
          display: 'flex'
        },
      }}
      >
        {dialogContent}
      </Dialog>
    );
  } else {
    return (
      <Box sx={{ width: "100%", height: "100%", minHeight: "40rem" }}>
        {dialogContent}
      </Box>
    );
  }
};

PdfViewer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  previewImage: PropTypes.string,
  useDialog: PropTypes.bool,
  isPdf: PropTypes.bool,
  setPageNumber: PropTypes.func,
  pageNumber: PropTypes.number,
  isVideo: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export default PdfViewer;
